import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import gsap from 'gsap'
import { callPostApi } from '../../api/ApiCaller'
import { SET_GAME_BONUS_RESULT_POST } from '../../api/ApiConst'
import { getAudioVolume } from '../../utils/CookiesUtils'
import Balloon, { BALLOON_W, BALLOON_H, RISE_SPEED } from './basket/Balloon'
import { EXIT_FANFARE_MS, playExitFanfare, warmExitFanfare } from './exitFanfare'
import './BasketBonus.css'

const BASE = 'ice-assets/basket-bonus'

// set to false to mute the looping background track while the bonus is open
const BG_MUSIC = true

// the asset name has a space in it, so it has to be escaped for CSS url()
const LAUNCH_BTN = `${BASE}/sprites/spriteButtonLaunch%201.png`

// The bear has two strips. The jump is the 7 frames sliced from the 4x2 atlas
// (458x347 each):
//   0 rest  1 squat  2 airborne  3..5 falling  6 SLAM — the plank flips.
// The penguin is catapulted on frame 6, not when the button is pressed.
const BEAR_STRIP = `${BASE}/sprites/spriteBear-strip.png`
const BEAR_FRAMES = 7
const BEAR_IMPACT = 6
const BEAR_FRAME_MS = [170, 120, 100, 90, 90, 95, 220]
// How long the slammed pose stays up before the bear settles back into its idle.
// It was 320ms, which snapped the see-saw flat while the bird it had just thrown
// was still climbing. video-look.mp4 holds it far longer: the slam lands at
// t=4.95s and the plank is still tilted at 6.03s — registered on the background
// and read at the tip, it goes up on the launch frame and does not come back down
// for over a second, by which time the next bird is on its way down the pillar.
// 900ms keeps it up for the whole flight and still clears with room to spare: the
// next bird cannot reach the plank sooner than ~2.3s after a throw (flight, then
// the hand-over, then SLIDE_TIME), and PLANK_TIP is measured against the IDLE
// cell, so the plank must be back at rest before it lands.
const BEAR_SLAM_HOLD = 900

// The idle is spriteBear.gif's own two frames, laid out side by side — but with
// the FIRST one nudged 3px right and 1px down. That offset is the whole point:
// the gif exports its two frames a few pixels apart, so playing it rocked the
// entire see-saw ~2px five times a second (measured on the plank at x 40, 80 and
// 200), and the penguin's spot on the plank tip is worked out once when it lands
// there — the board slid about under its feet the whole time it stood waiting.
// Re-registered, the plank matches between the frames to within its own
// antialiasing (244px differ, against 15650 across the bear) so the bear alone
// breathes. Regenerate with scripts/build-bear-idle.py if the gif is ever redrawn.
const BEAR_IDLE_STRIP = `${BASE}/sprites/spriteBear-idle.png`
const BEAR_IDLE_FRAMES = 2

// Three birds take turns, drawn at random but NOT evenly — see PENG_WEIGHTS.
// Each one is drawn TWICE, and which drawing is on screen says where the bird is:
//   `pillar` — sitting, legs splayed flat out to both sides. This is how it waits
//     up on the ice cap and how it comes down the face of it; ani_pinguim_bunda.wav
//     is named for the bird sliding down on its bottom.
//   `art` — standing. It stands up as its feet reach the plank, and it is thrown
//     standing.
// video-look.mp4 has both on screen at once over t 13.0-18.3s: the bird on the cap
// sits with its legs out flat while the bird on the plank below it stands. An
// earlier pass over the same clip matched that sitting bird against the STANDING
// art, concluded there was no second pose, and left the sitting files unused —
// that was wrong. The pillar bird never stands until it is down.
// The two poses are the two cells of spritePenguin00{1,2,3}.png. Two characters
// ship theirs already split out as Penguin-piller-*.png; the bow-tie one's is cut
// from its sheet by scripts/build-penguin-pillar-bow.py.
// The 228-tall files are drawn on one shared canvas with the feet at its foot,
// so bottom-anchoring plants every bird on the same ground line; the box is
// sized off that canvas HEIGHT and its width follows each image's own aspect —
// otherwise the wider ones get squeezed, and sizing off the width would make
// them different heights.
// spritePenguin.png is NOT on that canvas: it ships cropped tight to the figure.
// So it carries its own `h` (its real canvas height) and `scale` — how tall that
// canvas stands in the shared 228-unit scale. 134 comes off video-look.mp4,
// where this bird fits the plank at 73px tall against motu's 65px, and motu's
// drawn figure is 115 of its 228 units; 143/148 of the tight file therefore has
// to come out 73/65 as tall. Both fits put the feet on the same line (y 117 and
// y 119 of the same registered crop), which is the check that it holds.
// `feet` is where the sole line sits in the file, as a fraction of its canvas —
// the landing reads it to put the feet on the plank. Every art carries its own, and
// it is NOT simply the lowest drawn pixel: these birds have a toe that points down
// and away, and their two feet are drawn at different heights, while the board they
// stand on is a SLOPE. Hang the single lowest pixel on the wood and the bird
// balances on one toe tip with a visible gap under the rest of both feet — which is
// exactly what it looked like.
// So these are fitted instead: take the underside profile of the drawn feet (the
// bottom-most opaque row of every column in the lowest 12% of the file), lay it over
// the board's own top-surface profile at the same on-screen scale, and take the 25th
// percentile of the per-column contact offsets. First-contact leaves the far foot in
// the air, the median buries the near toe through the front edge of the board; the
// 25th percentile puts both feet down and lets only the leading toe overlap, which
// is how the reference draws it. scripts/ has no build step for this — it is a
// measurement, redone with a short script if the art is ever redrawn.
// PENG_FEET is only a fallback now; nothing in PENG_CHARS relies on it.
// `cx` is where the FIGURE is centred across the file, as a fraction of its width.
// The files are not padded symmetrically (a sitting bird's legs reach further one
// way than the other), so centring the box would stand the two poses of the same
// bird over slightly different points — and the swap at the landing would show it
// as a sideways twitch. Centring on `cx` instead keeps the bird still.
// `basketFrame` is this same bird's cell in spriteBalloon{Colour}.png, which
// already carries all three drawn sitting in the weave — so a landing needs no
// art of its own, only the right cell.
const PENG_CHARS = [
    {   // patlu — the thin one
        art: { src: `${BASE}/sprites/Penguin-patalu.png`, w: 128, cx: 0.523, feet: 0.937 },
        pillar: { src: `${BASE}/sprites/Penguin-piller-patalu.png`, w: 127, cx: 0.453, feet: 0.961 },
        basketFrame: 2,
        weight: 2,
    },
    {   // motu — round, head turned aside, long thin quiff
        art: { src: `${BASE}/sprites/Penguin-motu.png`, w: 123, cx: 0.480, feet: 0.948 },
        pillar: { src: `${BASE}/sprites/Penguin-piller-matu.png`, w: 138, cx: 0.467, feet: 0.983 },
        basketFrame: 1,
        weight: 3,
    },
    {   // the bow-tie one — square on to the camera, flippers down both sides
        art: {
            src: `${BASE}/sprites/spritePenguinsPort.png`,
            w: 137, h: 148, scale: 134, feet: 0.947, cx: 0.493,
        },
        // the only one whose sitting pose is not a shipped file of its own — it is
        // the left cell of spritePenguin001.png, on the shared 130x228 canvas
        pillar: { src: `${BASE}/sprites/Penguin-piller-bow.png`, w: 130, cx: 0.492, feet: 0.965 },
        basketFrame: 3,
        weight: 5,
    },
]
const PENG_ART_H = 228
// the box these were tuned against: 8% of the stage wide at 130x228. Every pose
// is drawn at that same height, and centred on the same point, so a bird does
// not jump sideways or change size when its pose changes.
const PENG_BOX_W = 8
const PENG_BOX_REF_W = 130
// Where the bird on the pillar is centred, in % of the stage. It is PLANK_TIP.x
// expressed in stage terms (15 + 0.17 x 30), because video-look.mp4 has the two
// birds stacked: over t 13.0-18.3s the pillar bird's feet centre on video x 97.8
// and the plank bird's on 98.6 — under a pixel apart on birds ~55px wide, from
// isotropic fits of our own art to a 119-frame registered stack. It used to be
// 22, which put the pillar bird a quarter of a bird-width to the RIGHT of the
// one below. 20.1 also sits it mid-cap rather than off its right end.
const PENG_BOX_CENTRE = 20.76

// Which bird a throw gets. Counted off video-look.mp4, reading the plank tip
// frame by frame (each frame registered on the see-saw log so the handheld
// camera comes out, then matched against the art): the bow-tie one holds the
// plank at 0.7-6.2s and 20.6-25.2s, motu at 11.4-18.6s, and the thin one never
// appears at all in the clip's 28s. Each bird keeps its drawing for its whole
// wait — no cycling — so it is the DRAW that is weighted, once per penguin.
// The thin one keeps the smallest share rather than none: it is a real draw in
// the game (the pillar showed it in the older recording), just a rare one.
const PENG_WEIGHTS = PENG_CHARS.map((c) => c.weight)
const PENG_WEIGHT_TOTAL = PENG_WEIGHTS.reduce((a, b) => a + b, 0)
const pickPengChar = () => {
    let r = Math.random() * PENG_WEIGHT_TOTAL
    for (let i = 0; i < PENG_WEIGHTS.length; i++) {
        r -= PENG_WEIGHTS[i]
        if (r < 0) return i
    }
    return PENG_WEIGHTS.length - 1
}
// how long the slide down the pillar face takes. Measured off gamevideo.mp4:
// it leaves the top at 21.67s and is down at ~22.47s.
const SLIDE_TIME = 0.8
// pulo_pinguim.wav opens with 0.4s of digital silence and only then hits — the
// envelope peaks at 0.4s and has decayed to nothing by 1.6s, against a 2.25s
// file. Played on the landing frame the thump arrives 0.4s late; played on the
// first frame of the slide it arrives 0.4s early. So start it this far ahead of
// the landing and the hit sits on it.
const PULO_LEAD = 0.4
// the drawn feet stop at y 220..225 of the 228-tall frame, so the box bottom is
// a sliver below the ground line — land on this, not on the box, or the penguin
// hovers just above whatever it is standing on
const PENG_FEET = 0.975

// the static single-pose sprite, used for the attempt row markers
const PENG_MARKER = `${BASE}/sprites/spritePenguin.png`

// where the plank's raised left tip sits inside the bear sprite, as a fraction
// of the sprite box (measured off the .gif). Read off the bear's live rect so
// the landing spot stays right whatever the stage aspect ratio is.
// x was 0.085, which put the bird half off the end of the plank — it stood past
// the raised tip with its feet hanging over the drop. 0.17 was the tip proper, and
// measured against video-look.mp4 it was the closer fit: there the bird stands 0.23
// of the way from the plank's tip to the log and 0.17 puts ours at 0.24. 0.192 is
// 0.29 of that run, so it sits the bird a little further in from the tip than the
// recording does — it is a deliberate call made on the running game rather than on
// the clip, which is a phone pointed at an angled monitor and cannot settle a
// difference this small. It is the value that puts the standing bird's `left` at
// 17% instead of 16.34%.
// NOTE: `left` is NOT the knob for this. plantOnPlank works the bird's transform
// out from the bear's live rect, so whatever `left` says is cancelled out again on
// the next landing or resize; the position on the plank lives here and nowhere else.
// y: at x 78 (= 0.17 x 462) the plank occupies rows 280..291 of 347 in the idle
// cell — 280 is its top OUTLINE, 291 its underside. It was 0.845 (row 293), which
// hung the feet under the board; then 0.807 (row 280), which is the other edge of
// the same problem: the toe line balanced on the outline itself, so the bird read
// as hovering rather than standing, and the LEFT foot hung visibly in the air
// because the board rises to the right while the feet are drawn level. 0.815 is
// row 283, a few pixels into the wood: both feet make contact and the toes overlap
// the board the way they do in the reference. 0.822 (row 285) is already too far —
// the toes disappear into the plank.
// y moves with x, because the board is a slope: at the old x (cell px 78) its top
// surface is row 281, at the new one (cell px 89) it is row 279. 0.8092 is row 281,
// which keeps the toes the same ~2px into the wood that 0.815 gave at the old x.
const PLANK_TIP = { x: 0.1921, y: 0.8092 }

// the row along the bottom: the same penguin repeated 5 times. `left` is the
// centre of each one, in % of the stage. The row is the CHANCE counter, not a
// score: one X lands per PULAR pressed, so a miss — or a tile the backend priced
// at 0 — crosses one off exactly like a basket does. Five throws, five Xs, every
// round. ATTEMPTS is that same count, and it is what runs the round out.
const ROW = [33, 41.5, 50, 58.5, 67]
const ATTEMPTS = ROW.length

// every balloon colour shipped in sprites/, cycled through as they spawn
const COLOURS = ['Red', 'Yellow', 'Blue', 'Green', 'Orange', 'Purple', 'DarkBlue']
// The balloons come up ONE AFTER ANOTHER, in two lanes, on a fixed beat — not at
// random. This used to spawn every 2.2s at a random x and a random 11-15s rise,
// which kept ~6 in the sky, let fast ones climb through slow ones and piled them
// on top of each other. What gubbare-ani-video.mp4 and the clean capture (youtube
// n0wAAQk3gPo) both show, measured 2026-09-17:
//   - a slot every 3s, alternating CENTRE, RIGHT, CENTRE, RIGHT...
//   - every other RIGHT slot stays empty, so the pattern is centre, right,
//     centre, nothing — 12s, three baskets. In the capture the centre lane
//     crosses 80% of the stage at 154.3, 160.5, 166.7, 172.7 and 178.8s, the
//     right lane at 169.3, and 163.6 / 175.7 pass with nothing. The phone clip's
//     arrivals (2, 5, 8, 14, 17, 20, 26, 29, 32, 38s) skip 11, 23 and 35 — the
//     same beat.
//   - lanes: the centre unit swings about a centre of 63.4% of the stage, the
//     right one about 84.4%; a unit is 20% wide, so those are `left` 53.4 / 74.4.
// The empty slot is NOT kept: it opened a hole a whole balloon deep after every
// third basket, and it read as the sky stalling. Every slot is filled, lanes
// alternating.
//
// What the 3s beat really is, though, is a SPACING, and a spacing in seconds does
// not survive onto our stage. In the capture 3s at Balloon's rise is 30% of the
// stage and a unit is 31% of it, so each basket goes up one unit height below the
// last: its balloons crest just under the basket above, on the other side. A
// reference still of the phone clip shows the same — balloon centre to basket
// foot ~240px, next unit's balloons ~280-330px lower. Our unit is drawn at its
// true proportions and comes out ~44% of a 16:9 stage (the recording is stretched
// sideways), so at 3s the next balloons rose BESIDE the basket above instead of
// under it, and the sky looked crowded — ~380px centre to foot against ~260-290px
// between units on our own still. So the gap is set in unit heights, and turned
// into time off the live stage at every spawn: SPAWN_GAP_UNITS of a unit height
// at RISE_SPEED. On 16:9 that is ~4.4s; a screen change is picked up on the next.
const SPAWN_GAP_UNITS = 1.0
// .bk-balloon's width, as a fraction of the stage — the unit height follows from it
const BALLOON_WIDTH = 0.20
const SPAWN_PATTERN = ['centre', 'right']
const LANES = {
    centre: { xPct: 53.4, side: 1 },
    right: { xPct: 74.4, side: -1 },
}
// Backstop only: if the risers ever stop advancing while the spawner keeps ticking —
// which is exactly what a backgrounded tab used to do — the sky filled with frozen
// baskets that all appeared at once on the way back. The pause below is the real
// fix; this makes sure no other stall can reproduce the pile. A unit lives ~19s on
// a 16:9 stage and one goes up every ~4.4s, so a healthy sky holds four or five
// counting the ones still below the floor and already past the top — more on a
// tall narrow screen, where the unit is small and they go up closer together.
const MAX_BALLOONS = 10

// the penguin lands in a basket around here; the nearest one wins the shot
const LAND_ZONE = { x: 0.65, y: 0.5 }

// Sound effects are pooled and decoded ONCE. A `new Audio()` per shot fetches and
// decodes the clip on the main thread, and acertou_cesta.wav did that exactly as
// the bird was dropping into a basket — an uncompressed .wav decode there costs
// several frames and reads as the animation braking on the landing. One element
// per file, rewound to replay, keeps that moment free.
const SFX_FILES = ['pulo_pinguim.wav', 'pula_urso.wav', 'acertou_cesta.wav']
const sfxPool = {}
const getSfx = (file) => {
    let a = sfxPool[file]
    if (!a) {
        a = new Audio(`assets/ice-bonus/${file}`)
        a.preload = 'auto'
        sfxPool[file] = a
    }
    return a
}

const playSfx = (file) => {
    const a = getSfx(file)
    a.volume = getAudioVolume()
    try { a.currentTime = 0 } catch (e) { /* not loaded yet — it starts at 0 anyway */ }
    a.play().catch(() => { })
    return a
}

/* The two clips a throw makes noise with, measured off the files: both 22050Hz mono,
   pula_urso.wav is 0.915s long but its 50ms envelope has decayed into the floor by
   0.75s, and acertou_cesta.wav is 3.057s long with sound only to 1.95s.
   LEAP_MS is when the cheer goes off, so that it follows the leap with nothing
   audible in between. CHEER_TAIL_MS is how long the cheer then sounds for — the file
   runs on to 3.057s but the last 1.1s of it is silence, and holding the fanfare for
   that silence would be a dead beat on the last throw, not a pause.
   For reference, the same window in the reference capture (youtube n0wAAQk3gPo, the
   basket bonus at 2:48-3:04): the bear's leap sounds at 174.50s, the bear slams at
   174.85s, the bird leaves the plank at 174.95s, the closing piece comes in at
   175.25s and runs unbroken ~8.4s to 183.7s, and the round is handed back at 184.3s.
   The piece is NOT timed off that measurement — it goes off on the landing, which is
   what the bonus is actually celebrating; see the flight's onComplete. */
const LEAP_MS = 915
const CHEER_TAIL_MS = 1950
/* How far INTO the cheer's tail the fanfare comes in. CHEER_TAIL_MS alone put the
   piece at press+2865ms, which is a clean hand-over but reads as a beat of waiting
   after the basket; a second earlier it lands on the catch instead, while the cheer
   is still ringing out under it. The overlap is deliberate and it is the only one
   left in the round — the leap is long finished, and this is the cheer's decay, not
   its attack. Raising it past CHEER_TAIL_MS would put the piece back on top of the
   cheer proper, which is the pile-up this whole path exists to stop. */
const EXIT_LEAD_MS = 1000

// FORÇA — 5-frame vertical strip, oscillates while aiming
const POWER = { img: `${BASE}/sprites/spritePower.png`, frames: 5 }
// TEMPO — 10-frame horizontal strip, draining over TEMPO_CYCLE.
// It is an ANIMATION, not a deadline. The pie used to run the round out when it
// hit the spent frame, which dropped a player who had not thrown yet straight
// back into the slot; now it simply refills and drains again, and only the five
// throws end the round.
// Rebuilt from spriteTime.png: that sheet is 1036px for 10 frames, a fractional
// 103.6px pitch, and its 92px pie sits off-centre in the cell with the next
// frame's art bleeding in. spriteTime_clean.png is the same 10 pies cropped to
// their 92x92 circle on an exact 92px pitch, so one square box centred on the
// socket lines every frame up.
// The pie drains by an exact tenth a frame — counting the opaque pixels per cell
// of spriteTime_clean.png gives 6600, 5940, 5281 ... 660, a flat -660 each time,
// and the last frame is the spent clock: a thin red sliver on the empty socket.
const TIME = { img: `${BASE}/sprites/spriteTime_clean.png`, frames: 10 }
// one full drain, full pie to red sliver — the length the video's basket scene
// runs. The frames are paced over `frames - 1` gaps, not `frames`: at
// TEMPO_CYCLE / frames the sliver came up at 64.8s, 7.2s short of its own clock.
const TEMPO_CYCLE = 72000
const TIME_STEP = TEMPO_CYCLE / (TIME.frames - 1)

// how long the SLAM will wait for getBonusResult before giving up on it. The call
// goes out at PULAR and the bear takes ~665ms to come down on the plank, so this is
// only ever reached on a stalled network — and a shot with no answer is not a win.
const SHOT_ANSWER_WAIT = 1500
const SHOT_ANSWER_POLL = 50

// gubbare-ani-video.mp4 opens on an empty sky and the first basket's balloons peek
// over the floor of the scene at 1.25s (green, centre lane; then purple on the right
// at ~4.75s and red back in the centre at ~7.75s — the 3s beat). A unit starts
// 1.1 of its own height below the stage and its balloons crest ~0.04 of it down
// the frame, so it needs 0.14 unit heights to show: ~0.6s at Balloon's rise on a
// 16:9 stage. Launched this early, it shows at the video's moment.
const FIRST_BALLOON_DELAY = 650


let balloonId = 0

function BasketBonus({ gameSessionRecordId, bonusType, onBonusRoundCompleted }) {
    const [ganho, setGanho] = useState(0)
    const [balloons, setBalloons] = useState([])
    // PULARs pressed. It is the round's only count: it runs the round out at
    // ATTEMPTS and it is what the row along the bottom draws. There used to be a
    // second `hits` count for the row, which meant a round of five misses ended
    // with an untouched row — five chances spent and nothing crossed off.
    const [attempt, setAttempt] = useState(0)
    const [pengKey, setPengKey] = useState(0)     // remounts a fresh penguin per attempt
    const [onPlank, setOnPlank] = useState(false) // penguin is loaded, PULAR armed
    // the active bird has stood up, which happens the instant its feet reach the
    // plank. Until then it is drawn sitting — that is the pose it waits in on the
    // pillar and slides down in. It is NOT `onPlank`: PULAR clears that on the way
    // into the throw, and the bird is thrown standing, so keying the pose off it
    // would sit the bird back down in mid-air. Cleared per bird, in nextPenguin.
    const [standing, setStanding] = useState(false)
    const [bearFrame, setBearFrame] = useState(null)  // null = idle gif
    const [power, setPower] = useState(0)         // FORÇA 0..4
    const [timeStep, setTimeStep] = useState(0)   // TEMPO 0..9, 9 = the red sliver
    const [congrats, setCongrats] = useState(false)
    const [over, setOver] = useState(false)
    // the page itself is away — tab switched, window minimised, phone locked. The
    // scene is frozen while this is true (see the effect below).
    const [hidden, setHidden] = useState(false)
    // the round has run out AND the shot in the air has resolved, so there is no
    // bird left anywhere — none on the plank, none queued on the pillar. Separate
    // from `over` on purpose: `over` goes true the moment the last PULAR is
    // pressed, and the bird thrown by that press still has a second in the air.
    const [pengGone, setPengGone] = useState(false)
    // a penguin is always queued on the pillar behind the one on the plank; it
    // only vanishes for the moment the active one takes its place and hops down
    const [cliffFull, setCliffFull] = useState(false)
    // which bird is in play, and which one is waiting its turn up on the pillar.
    // The waiting one is the next to go, so its draw has to be made now and kept
    // — it is already on screen when the hand-over happens.
    const [activeChar, setActiveChar] = useState(pickPengChar)
    const [queuedChar, setQueuedChar] = useState(pickPengChar)

    const pengRef = useRef(null)
    const cliffRef = useRef(null)   // the one waiting on the pillar
    const bearRef = useRef(null)
    const balloonEls = useRef({})
    const bearTimer = useRef(null)
    const catapultRef = useRef(null)
    const slamRef = useRef(null)      // same reason: the slam reschedules itself
    const shotWaitRef = useRef(null)   // the plank flip waiting on getBonusResult
    const powerDir = useRef(1)
    const bgRef = useRef(null)         // the looping track, so the pause can silence it
    const stageRef = useRef(null)      // .basket-bonus, measured to space the balloons
    // the exit fanfare is playing: the round is over and the loop stays down, even
    // if the tab comes back to the front while the piece is still running
    const exitingRef = useRef(false)
    const exitTimerRef = useRef(null)
    const fanfareStartRef = useRef(0)
    // when the throw in progress was launched, and whether it was the last one.
    // Together they say when its leap/cheer chain will have finished, which is the
    // earliest the exit fanfare may come in — see `startExit`.
    const throwStartRef = useRef(0)
    const lastThrowRef = useRef(false)
    const cheerTimerRef = useRef(null)
    // when the catch cheer starts sounding, so the fanfare can take over from it
    // rather than cut across it
    const cheerStartRef = useRef(0)
    const exitStartRef = useRef(null)
    // the fanfare is actually running. The close-out measures what is left of the
    // piece from `fanfareStartRef`, so it must not be allowed to do that sum before
    // there is a start time to subtract — this is what holds it back.
    const [fanfareOn, setFanfareOn] = useState(false)
    // the hand-back is scheduled; see the close-out effect for why it only ever
    // happens once
    const closingRef = useRef(false)
    // the round's opening empty sky is owed once, not again on every tab-back
    const skyOpened = useRef(false)
    // where the spawner is in SPAWN_PATTERN; kept across a tab-back so the beat
    // carries on instead of starting over
    const spawnSlot = useRef(0)
    // a bird is in the air: set at PULAR, cleared when its flight hands over. The
    // clock has to know, because a throw still going closes the round on its own
    // and stepping in on top of it would unmount the bird mid-flight.
    const inFlightRef = useRef(false)
    // was the shot a catch? Written by the catapult, which is the thing that decides
    // it, and read immediately afterwards in the same slam — the cheer and the exit
    // fanfare are both scheduled off it there, so neither can be fired against a
    // stale answer
    const hitRef = useRef(false)
    // The running total lives in the ref — the flight's onComplete would read a
    // stale `ganho`, and the count-up writes its number straight into the DOM
    // node below, so the state only catches up once it has settled.
    const ganhoRef = useRef(0)
    const ganhoElRef = useRef(null)
    // same reason: the landing runs from the flight's onComplete, and it has to
    // know which bird it is putting in the basket
    const activeCharRef = useRef(0)
    activeCharRef.current = activeChar
    // and again: the flight's onComplete decides whether to load another bird, and
    // its closure was made a render or two before `over` was set
    const overRef = useRef(false)
    overRef.current = over
    // slideToPlank is a `useCallback` with no deps, so it cannot read `attempt`
    const attemptRef = useRef(0)
    attemptRef.current = attempt
    // the prize for the shot currently in the air, and what the round pays in total.
    // Both are refs: a landing runs out of the flight's onComplete, whose closure is
    // a render or two old.
    const shotPrizeRef = useRef(0)
    const totalWinRef = useRef(0)

    /* ---- one getBonusResult per throw: the tile IS the attempt (0..4). The bear's
       leap plus the flight is ~1.9s, so the answer is in hand long before the bird
       reaches a basket ---- */
    const requestShotPrize = (tile) => {
        // null = still asking. The catapult holds the throw until this is a number,
        // because the answer is what decides whether the bird is caught at all.
        shotPrizeRef.current = null
        if (!gameSessionRecordId) { shotPrizeRef.current = 0; return }   // no round to ask about
        const payload = { bonusType, recordId: gameSessionRecordId, tileNumber: tile }
        callPostApi(SET_GAME_BONUS_RESULT_POST, payload, {}, (response) => {
            if (response?.errorCode !== 0) {
                console.error('getBonusResult failed:', response?.message || 'Unknown error')
                shotPrizeRef.current = 0   // resolved, just not a win
                return
            }
            const res = response?.responsePacket
            shotPrizeRef.current = Math.round(res?.bonusDetails?.bonus || 0)
            totalWinRef.current = res?.totalWinnings || totalWinRef.current
        })
    }

    useEffect(() => () => {
        clearTimeout(bearTimer.current)
        clearTimeout(shotWaitRef.current)
        clearTimeout(exitTimerRef.current)
        clearTimeout(cheerTimerRef.current)
        clearTimeout(exitStartRef.current)
    }, [])

    /* ---- warm everything that first appears mid-flight, so nothing is fetched
       or decoded while the bird is in the air ---- */
    useEffect(() => {
        SFX_FILES.forEach((f) => getSfx(f).load())
        warmExitFanfare()
        const warm = [`${BASE}/msg_congrats.png`, `${BASE}/x.png`]
        warm.forEach((src) => { const img = new Image(); img.src = src })
    }, [])

    /* ---- background music for as long as the bonus is open ---- */
    useEffect(() => {
        if (!BG_MUSIC) return
        const bg = new Audio('assets/ice-bonus/fundo_bonusI.wav')
        bg.loop = true
        bg.volume = getAudioVolume()
        bgRef.current = bg

        // play() is rejected — silently, the promise is the only signal — while the
        // document has no user activation, which is the normal state when the game
        // runs inside a lobby iframe. The first gesture that reaches us re-arms it,
        // and the listeners come off as soon as the loop is actually running.
        const GESTURES = ['pointerdown', 'keydown', 'touchstart']
        const stopListening = () => GESTURES.forEach((e) => window.removeEventListener(e, start))
        const start = () => { bg.play().then(stopListening).catch(() => { }) }
        start()
        GESTURES.forEach((e) => window.addEventListener(e, start))

        return () => {
            stopListening()
            bg.pause()
            bg.currentTime = 0
            bgRef.current = null
        }
    }, [])

    /* ---- the page went away: freeze the round until it comes back.
       Everything that MOVES here is driven by gsap, which is stepped by
       requestAnimationFrame — and a hidden tab gets no frames. Everything that
       COUNTS is a setInterval, and those keep firing (throttled) with the tab in
       the background. Left alone the two drift apart: the balloons stood still
       while the spawner kept adding to them, so a player who tabbed away came
       back to a wall of baskets sitting on the floor of the scene, and the TEMPO
       pie had meanwhile drained on a game nobody could see and dropped them back
       into the slot. So park the whole thing on `visibilitychange` — the gsap
       timeline (every rise, every flight, every queued delayedCall), the three
       intervals below, and the music with it.
       The clock restarting its current tick on the way back cannot be milked for
       money: ATTEMPTS, not TEMPO, is what caps a round's payout. ---- */
    useEffect(() => {
        const sync = () => {
            const away = document.visibilityState === 'hidden'
            setHidden(away)
            if (away) gsap.globalTimeline.pause()
            else gsap.globalTimeline.resume()
        }
        document.addEventListener('visibilitychange', sync)
        sync()   // mounted with the tab already in the background
        return () => {
            document.removeEventListener('visibilitychange', sync)
            // never leave the rest of the game frozen behind us
            gsap.globalTimeline.resume()
        }
    }, [])

    useEffect(() => {
        const bg = bgRef.current
        if (!bg) return
        if (hidden || exitingRef.current) bg.pause()
        else bg.play().catch(() => { })
    }, [hidden])

    /* ---- FORÇA sweeps up and down while a penguin is waiting on the plank ---- */
    useEffect(() => {
        if (!onPlank || over || hidden) return
        const t = setInterval(() => {
            setPower((p) => {
                if (p >= POWER.frames - 1) powerDir.current = -1
                else if (p <= 0) powerDir.current = 1
                return p + powerDir.current
            })
        }, 170)
        return () => clearInterval(t)
    }, [onPlank, over, hidden])

    /* ---- TEMPO drains, refills, drains again, for as long as the bonus is open.
       It is the panel's animation and nothing else — reaching the spent frame
       used to end the round, and that is what threw a player who had not got
       round to a throw yet back into the slot. Wrapping to 0 keeps the dial alive
       without it deciding anything. Paused with the page, like everything else:
       a dial that jumped several frames on the way back would read as the round
       having run on while nobody was watching, which is the very thing this
       stopped doing. ---- */
    useEffect(() => {
        if (hidden) return
        const t = setInterval(() => {
            setTimeStep((s) => (s >= TIME.frames - 1 ? 0 : s + 1))
        }, TIME_STEP)
        return () => clearInterval(t)
    }, [hidden])

    /* ---- the round is over when every attempt is used. THROWS are the only
       thing that ends it. ---- */
    useEffect(() => {
        if (attempt >= ATTEMPTS) setOver(true)
    }, [attempt])

    /* ---- close-out for a round that ends with nothing in the air.
       `pengGone` is what actually shuts the bonus (see the effect below), and it
       is only ever set from nextPenguin — which runs off a flight's onComplete.
       So it only comes up when the round ended on a throw. With the clock gone
       that is the only way `over` can go true, so this no longer fires in
       practice; it stays as the backstop for any future path that ends the round
       with the bird just standing on the plank. Nothing in the air means nothing
       is coming to close it, so close it here. ---- */
    useEffect(() => {
        if (!over || inFlightRef.current) return
        // true: the bird is still standing on the plank, so fade it rather than
        // blinking it out from under the player
        endRoundRef.current(true)
    }, [over])

    /* ---- the exit fanfare. It takes over from the background loop and plays across
       everything that is left — the bird dropping into its basket, PARABÉNS, the
       GANHO counting up, the scene clearing — so the piece is running under the end
       of the round rather than after it. Not started on the cleared scene, or its 8s
       would all be spent on a still picture.
       It is NOT started on `over`, which is what it used to do. `over` is set off the
       attempt counter and that counter goes up on the PULAR press, so the piece opened
       on the same beat the last throw began: its first four seconds played underneath
       pula_urso.wav and then acertou_cesta.wav, three voices at once, and the
       background loop was cut mid-phrase to make room for the pile-up.
       It goes off on the landing — the frame the last bird drops into its basket —
       held just long enough for the catch cheer to finish sounding; the flight's
       onComplete does that, see there. ---- */
    const startExit = () => {
        if (exitingRef.current) return
        exitingRef.current = true
        fanfareStartRef.current = Date.now()
        playExitFanfare()
        const bg = bgRef.current
        if (bg) { bg.pause(); bg.currentTime = 0 }
        setFanfareOn(true)
    }
    const startExitRef = useRef(startExit)
    startExitRef.current = startExit

    /* ---- backstop, for a round that ends with nothing in the air. A throw schedules
       its own fanfare off the back of its sounds; this is the path where there are no
       sounds to wait for, and it is the same condition the close-out above uses. ---- */
    useEffect(() => {
        if (!over || inFlightRef.current) return
        startExitRef.current()
    }, [over])

    /* ---- round spent AND the last shot has resolved: hand the win back, but not
       before the fanfare has played out. It used to be a flat 2s beat on the final
       GANHO, which put the player back on the reels with ~6s of the piece still to
       run — that is not the machine handing a won bonus over, it is the bonus
       vanishing out from under its own music.
       Two things are deliberate. The wait is a TIMER, not the element's 'ended'
       event: a play() the browser rejects for want of user activation never fires
       'ended', and the round would sit in the bonus with nothing to get it out. And
       it is latched and never cancelled by a re-run — `onBonusRoundCompleted` is an
       inline arrow in the wrapper, so it is a new function on every render the game
       does, and a plain effect would push the close another few seconds out at each
       one. Only the unmount clears it (see the cleanup effect above), and the only
       thing that unmounts this bonus is this hand-back. ---- */
    useEffect(() => {
        // `fanfareOn` is in here because the scene can now finish clearing BEFORE the
        // piece starts — the fanfare waits out the leap and the cheer, and on a caught
        // last throw that is ~4s, while the bird is in its basket by ~2s. Without this
        // the sum below would run against an unset `fanfareStartRef` (0), come out at
        // minus fifty-odd years, clamp to 0, and hand the round back instantly — the
        // player would be on the reels before the fanfare had played a note.
        if (!over || !pengGone || !fanfareOn || closingRef.current) return
        closingRef.current = true
        // whatever is left of the piece — usually a few seconds, and 0 if the scene
        // somehow took longer to clear than the music took to play
        const left = EXIT_FANFARE_MS - (Date.now() - fanfareStartRef.current)
        exitTimerRef.current = setTimeout(() => {
            if (onBonusRoundCompleted) onBonusRoundCompleted(totalWinRef.current || ganhoRef.current)
        }, Math.max(0, left))
    }, [over, pengGone, fanfareOn, onBonusRoundCompleted])

    /* ---- the penguin comes down off the ice pillar onto the plank's tip.
       Not a leap: gamevideo.mp4, stepped at 15fps over 20.8-23.4s, shows it go
       STRAIGHT DOWN the face of the pillar, sitting the whole way — no hop up
       first, no tuck, no landing squash. That is what ani_pinguim_bunda.wav is
       named for: it slides down on its bottom. This used to arc a full 99px
       upwards before falling, which reads as a jump across a gap the scene does
       not have. It is the SITTING drawing that comes down; standing up is the last
       thing that happens, once the feet are there — see `standing`. ---- */
    const slideToPlank = useCallback(() => {
        const peng = pengRef.current
        const bear = bearRef.current
        if (!peng || !bear) return

        const p = peng.getBoundingClientRect()
        const b = bear.getBoundingClientRect()

        // read off the ref, not the state: this runs from a delayedCall, and
        // taking `activeChar` as a dependency would rebuild the callback and
        // re-fire the slide every hand-over. The bird on its way down is sitting,
        // so it is the sitting drawing's own measurements that place it.
        const art = PENG_CHARS[activeCharRef.current].pillar
        // land with the feet on the plank tip, the figure centred over it
        const dx = (b.left + b.width * PLANK_TIP.x) - (p.left + p.width * (art.cx || 0.5))
        const dy = (b.top + b.height * PLANK_TIP.y) - (p.top + p.height * (art.feet || PENG_FEET))

        const tl = gsap.timeline({
            // it arrives and stands up on the same frame; the standing drawing is
            // a different size, so its feet are planted again below before paint
            onComplete: () => { setStanding(true); setOnPlank(true) },
        })
        // the drop is the clock; the small sideways drift just rides along with it
        tl.to(peng, { y: dy, duration: SLIDE_TIME, ease: 'power1.in' }, 0)
        tl.to(peng, { x: dx, duration: SLIDE_TIME, ease: 'none' }, 0)
        // pulo_pinguim.wav is the landing, not the drop, so it is started early
        // enough for its hit to fall ON the plank rather than above it — see
        // PULO_LEAD. Scheduled on the timeline so it tracks SLIDE_TIME.
        tl.call(() => playSfx('pulo_pinguim.wav'), null, Math.max(0, SLIDE_TIME - PULO_LEAD))
        // The pillar is clear from the moment it starts down, so the next bird can
        // take its place up there — but only if there IS a next one. Once
        // ATTEMPTS - 1 throws are spent the bird now sliding down is the last, and
        // nothing should be standing behind it: the pillar stays bare from here.
        // Counted off the throws, not the baskets — on baskets, a round that missed
        // everything kept queueing a bird that could never be thrown.
        tl.call(() => {
            if (attemptRef.current < ATTEMPTS - 1) setCliffFull(true)
        }, null, SLIDE_TIME * 0.3)
    }, [])

    /* ---- put the standing bird's soles on the plank tip.
       ABSOLUTE, not a correction on top of whatever transform is already there:
       the transform is zeroed, the untransformed box measured, and the offset
       written fresh. That makes it safe to call as often as we like, which is the
       whole point — see the resize effect below. ---- */
    const plantOnPlank = useCallback(() => {
        const peng = pengRef.current
        const bear = bearRef.current
        if (!peng || !bear) return

        const art = PENG_CHARS[activeCharRef.current].art
        gsap.set(peng, { x: 0, y: 0 })
        const p = peng.getBoundingClientRect()
        const b = bear.getBoundingClientRect()
        // Both boxes get their height from `aspect-ratio` over a % width, so they
        // are the right size from first layout — unless something upstream has not
        // settled yet, in which case they measure 0 and the sums below would plant
        // the bird a whole body-height above the plank and leave it there. Rather
        // than bake that in, leave the bird where it is and try again next frame;
        // the resize observer will have another go too.
        if (!p.height || !b.height) {
            requestAnimationFrame(() => plantRef.current && plantRef.current())
            return
        }
        gsap.set(peng, {
            x: (b.left + b.width * PLANK_TIP.x) - (p.left + p.width * (art.cx || 0.5)),
            y: (b.top + b.height * PLANK_TIP.y) - (p.top + p.height * (art.feet || PENG_FEET)),
        })
    }, [])
    // the retry above reschedules itself, so it needs a stable handle
    const plantRef = useRef(null)
    plantRef.current = plantOnPlank

    /* standing up swaps in a drawing of a different size and shape, and the
       transform that brought the bird down was worked out for the sitting one, so
       the feet are planted again the moment the pose changes — in a layout effect,
       before the browser paints, so the swap reads as a stand-up, not a hop. */
    useLayoutEffect(() => {
        if (!standing) return
        plantOnPlank()
    }, [standing, plantOnPlank])

    /* ---- and again whenever the stage changes size.
       The bird's spot on the plank is a PIXEL transform, worked out once from the
       bear's rect at the moment it lands, while every other thing in the scene is
       positioned in % and rescales on its own. So the moment the stage is a
       different size from when the bird landed, the two disagree and the bird is
       left standing in mid-air just up and to the left of the plank tip — which is
       exactly what it looks like. And this stage is `position: fixed; inset: 0`,
       i.e. the viewport itself: a phone hiding its URL bar, a rotation, a window
       drag or a zoom all resize it under a bird that has already landed.
       ResizeObserver also fires once when it starts observing, so this doubles as a
       second chance at the landing if the first rect was read before the layout had
       settled. The flight owns the transform once the bird is off the plank, so it
       is left alone from PULAR onwards. ---- */
    useEffect(() => {
        if (!standing) return
        const stage = pengRef.current && pengRef.current.parentElement
        if (!stage || typeof ResizeObserver === 'undefined') return
        const ro = new ResizeObserver(() => {
            if (!inFlightRef.current) plantOnPlank()
        })
        ro.observe(stage)
        return () => ro.disconnect()
    }, [standing, plantOnPlank])

    /* Fire the jump once the sprites are on screen. The rects are only meaningful
       after the penguin has decoded — it is `height: auto`, so before its image
       loads it measures 0 tall and the landing spot comes out wrong. The bear
       does not need waiting on: it is a background-image div with a fixed
       `aspect-ratio`, so its box is the right size from first layout. */
    useEffect(() => {
        if (!pengRef.current) return
        // The active penguin mounts exactly where the queued one is standing and
        // takes over from it, so it needs no entrance of its own — the queued
        // penguin is hidden on the same frame and the swap is invisible.
        // No waiting on image decode: both the penguin and the bear are
        // background-image divs with a fixed aspect-ratio, so their boxes are the
        // right size from first layout and the rects can be trusted straight away.
        // The opening gets a beat to settle; later hand-overs are quick.
        const call = gsap.delayedCall(pengKey === 0 ? 0.7 : 0.25, slideToPlank)
        return () => call.kill()
    }, [slideToPlank, pengKey])

    /* ---- balloons drift up the right of the scene, cycling every colour ---- */
    useEffect(() => {
        const spawn = () => {
            const lane = SPAWN_PATTERN[spawnSlot.current % SPAWN_PATTERN.length]
            spawnSlot.current += 1
            const id = ++balloonId
            setBalloons((b) => (b.length >= MAX_BALLOONS ? b : [...b, {
                id,
                colour: COLOURS[id % COLOURS.length],
                ...LANES[lane],
                frame: 0,
                prize: null,
            }]))
        }
        // ms until the next basket: SPAWN_GAP_UNITS of a unit height at the rise,
        // off the stage as it is right now
        const gap = () => {
            const st = stageRef.current
            if (!st || !st.clientHeight) return 4400
            const unitH = (st.clientWidth * BALLOON_WIDTH * BALLOON_H) / BALLOON_W
            return (SPAWN_GAP_UNITS * unitH * 1000) / (RISE_SPEED * st.clientHeight)
        }
        if (hidden) return
        // The first basket goes up FIRST_BALLOON_DELAY into the round. That opening
        // beat is owed once; coming back from a backgrounded tab picks the cadence
        // up where it left off instead.
        let timer
        const tick = () => {
            spawn()
            timer = setTimeout(tick, gap())
        }
        if (skyOpened.current) {
            timer = setTimeout(tick, gap())
        } else {
            timer = setTimeout(() => {
                skyOpened.current = true
                tick()
            }, FIRST_BALLOON_DELAY)
        }
        return () => clearTimeout(timer)
    }, [hidden])

    const removeBalloon = (id) => {
        setBalloons((b) => b.filter((x) => x.id !== id))
        delete balloonEls.current[id]
    }

    /* ---- PULAR: the bear squats, jumps, and slams the plank ---- */
    const onLaunchClick = () => {
        if (!onPlank || over) return
        setOnPlank(false)
        // from here until the flight hands over, a bird is in the air — the clock
        // must not close the round out from under it
        inFlightRef.current = true
        // Both are read at the slam, to work out when this throw stops making noise.
        // `attempt` is still the pre-increment count here, so the LAST throw is the
        // one taken while the counter reads one short of the full row.
        throwStartRef.current = Date.now()
        lastThrowRef.current = attemptRef.current >= ATTEMPTS - 1
        // `attempt` is still the pre-increment count, so it IS this throw's tile
        requestShotPrize(attempt)
        setAttempt((a) => a + 1)
        // The bear's leap. The basket cheer still comes straight off the back of it,
        // seamlessly, but it is scheduled from the slam rather than chained on this
        // element's 'ended' — see `slam`. The chain could not survive a slow answer:
        // 'ended' fires at LEAP_MS, the slam is what sets `hitRef`, and the slam waits
        // for getBonusResult, so any answer slower than the bear's six frames left the
        // handler reading a `hitRef` that was still false and the cheer never played
        // at all on a shot that had just been caught.
        hitRef.current = false
        playSfx('pula_urso.wav')

        let f = 0
        const step = () => {
            // the slam is its own step: it may have to wait, and nothing about the
            // see-saw may move until it can throw — see `slam`
            if (f >= BEAR_IMPACT) { slamRef.current(); return }
            setBearFrame(f)
            const hold = BEAR_FRAME_MS[f]
            f += 1
            bearTimer.current = setTimeout(step, hold)
        }
        step()
    }

    /* ---- the slam: the plank flips and the bird goes, on the SAME frame.
       This used to show frame 6 and then call the catapult, which held a frame at a
       time until getBonusResult came back — so on any slow answer the plank flew up
       and the bird stayed standing on it, and if the answer took longer than the
       320ms idle reset the see-saw even dropped flat again before the throw. That is
       the "plank first, penguin after" the scene must never show.
       video-look.mp4 settles the order. Registered on the background and read at the
       plank tip, the plank sits flat through t=4.881 and 4.917 with the bird on it;
       at 4.953 the tip has come up ~22px AND the bird is already a body-height above
       it. One frame, both things. So the wait belongs BEFORE the flip: the bear
       hangs on its last pre-slam frame until the answer is in, and the flip and the
       throw then go off together. ---- */
    const slam = (waited = 0) => {
        // The answer is the throw: a tile that paid 0 is a bird that falls. Hold on
        // the frame before the slam rather than animating the wrong outcome.
        if (shotPrizeRef.current === null && waited < SHOT_ANSWER_WAIT) {
            // its own timer: `bearTimer` is overwritten by the idle reset below, and
            // the wait would be left untracked
            shotWaitRef.current = setTimeout(() => slamRef.current(waited + SHOT_ANSWER_POLL), SHOT_ANSWER_POLL)
            return
        }
        setBearFrame(BEAR_IMPACT)
        catapultRef.current()
        // the bear settles back into its looping idle, but not before the flight
        bearTimer.current = setTimeout(() => setBearFrame(null), BEAR_SLAM_HOLD)

        /* The cheer is scheduled from here, because this is the first frame that
           knows whether there is one: the catapult has just decided the shot, so
           `hitRef` is now the answer. The offset is measured from the PULAR press,
           not from here — the leap started on the press and runs on its own clock,
           while this frame can be up to SHOT_ANSWER_WAIT late if the backend took its
           time, and timing off a frame that drifts would leave a gap on a slow answer.
           A timer, not the leap element's 'ended': a play() the browser rejects for
           want of user activation never fires 'ended' at all. */
        const since = Date.now() - throwStartRef.current

        /* EVERY caught throw gets the cheer, the last one included. It was taken off
           the last throw for a while, on the grounds that the reference plays one
           unbroken piece there — but the cheer IS the sound of the catch, and the fifth
           bird dropping into a basket in silence is the one thing a player notices at
           once: four throws land with a sound, the fifth lands with nothing. The
           fanfare waits for it instead; see the flight's onComplete.
           It goes off when the leap stops sounding, or right here if a slow
           getBonusResult has already pushed us past that. It cannot be chained on the
           leap element's 'ended' the way it used to be — 'ended' fires at LEAP_MS, and
           `hitRef` is not set until this frame, so any answer slower than the bear's
           six frames left the handler reading a `hitRef` that was still false and the
           cheer never played at all on a shot that had just been caught. */
        cheerStartRef.current = throwStartRef.current + Math.max(LEAP_MS, since)
        if (hitRef.current) {
            clearTimeout(cheerTimerRef.current)
            cheerTimerRef.current = setTimeout(() => playSfx('acertou_cesta.wav'),
                cheerStartRef.current - Date.now())
        }
    }

    /* ---- the throw itself. getBonusResult has already come back by the time this
       runs — `slam` does the waiting, so that the flip and the throw stay one event
       — and it decides whether a basket catches the bird or it drops through ---- */
    const catapult = () => {
        const peng = pengRef.current
        const stage = peng && peng.parentElement
        if (!peng || !stage) return

        const s = stage.getBoundingClientRect()
        const p = peng.getBoundingClientRect()

        // what this tile paid — anything above 0 is a catch, 0 is a drop
        const prize = shotPrizeRef.current || 0

        // The nearest empty basket is the aim whatever the tile paid — a shot worth
        // nothing still has to be thrown AT a basket, or the miss reads as the bird
        // being lobbed at nothing in particular.
        const ideal = { x: s.left + s.width * LAND_ZONE.x, y: s.top + s.height * LAND_ZONE.y }
        let best = null
        balloons.forEach((b) => {
            const el = balloonEls.current[b.id]
            if (!el || b.frame !== 0) return
            const r = el.getBoundingClientRect()
            // the basket is the lower third of the sprite
            const c = { x: r.left + r.width / 2, y: r.top + r.height * 0.82 }
            const d = Math.hypot(c.x - ideal.x, c.y - ideal.y)
            if (!best || d < best.d) best = { b, el, c, d }
        })
        // caught only if the backend paid for this tile; a 0 flies the same arc at
        // the same basket and slips past its rim instead of dropping in
        const caught = prize > 0 && !!best
        hitRef.current = caught   // the slam reads this to time the cheer and the exit

        // a miss carries on past the basket and falls, so it needs longer in the air
        const flight = caught ? 1.0 : 1.45
        // of which this much is the arc; the rest is the drop out of the scene
        const MISS_AT = 0.68
        const from = { x: p.left + p.width / 2, y: p.top + p.height / 2 }

        // Where the bird has to be when it arrives — read LIVE, every frame.
        // This used to be predicted once at launch from (stage + balloon height) /
        // rise, which under-counts how far the balloon really travels: it starts
        // BELOW the stage (Balloon's fromTo puts it at 1.1x its own height) so its
        // true run is stage + 2.1x that height. The bird therefore finished its
        // dive short of the basket, faded out there, and the sprite appeared in the
        // weave a step further up — which reads as the bird coming down, then going
        // back up, and only then sitting. Reading the rect also tracks the balloon's
        // sideways sway, which the old aim ignored completely.
        // With nothing in the sky to aim at, the arc goes up off the plank and comes
        // down in open air a little to the right.
        const nowhere = { x: from.x + s.width * 0.22, y: s.top + s.height * 0.55 }
        const target = () => {
            if (!best) return nowhere
            const r = best.el.getBoundingClientRect()
            // the basket is the lower third of the sprite
            if (caught) return { x: r.left + r.width / 2, y: r.top + r.height * 0.82 }
            // Grazing the NEAR rim, and never getting past it. The bird is thrown
            // from the plank on the left, so a miss aimed beyond the basket has to
            // fly over the top of it first — which is what made the drop look like
            // it was thrown well past and fell in open air. It comes down short
            // instead, against the side it reaches first.
            // Balloon.jsx scanned the weave off the sprite: x 33..172 and y 182..259
            // of the 212x259 frame, so the near edge is at 0.16 of the box and the
            // rim line at 0.70. 0.09 sets the bird just outside that edge, level
            // with the rim.
            return { x: r.left + r.width * 0.09, y: r.top + r.height * 0.72 }
        }

        // the video's arc peaks near the top of the screen, well above the target.
        // Sized off the aim at launch: the arc's height is a look, it does not need
        // to chase the basket the way the landing point does.
        const t0 = best ? best.c : nowhere
        const peak = Math.max(s.height * 0.42, (from.y - t0.y) + p.height)
        const apexY = from.y - peak

        // the transform the slide down the pillar left behind — the flight moves
        // the bird from there, so every offset below is added on top of it
        const base = { x: gsap.getProperty(peng, 'x'), y: gsap.getProperty(peng, 'y') }
        const up = gsap.parseEase('power2.out')
        const down = gsap.parseEase('power2.in')
        const drop = gsap.parseEase('power2.in')
        const APEX_AT = 0.45   // fraction of the arc spent climbing

        // one point on the arc, `u` running 0..1 from the plank to `g`
        const arcAt = (u, g) => ({
            x: from.x + (g.x - from.x) * u,
            y: u < APEX_AT
                ? from.y + (apexY - from.y) * up(u / APEX_AT)
                : apexY + (g.y - apexY) * down((u - APEX_AT) / (1 - APEX_AT)),
        })

        // where the arc ended, frozen at the moment the fall starts. It CANNOT be
        // re-read per frame like the aim is: the balloon keeps climbing, so a live
        // reference would drag the falling bird up with it instead of letting it drop.
        let fallFrom = null

        const st = { t: 0 }
        const tl = gsap.timeline({
            onComplete: () => {
                if (caught) landInBasket(best.b)
                // Paid, but the sky happened to hold no empty basket to land in. The
                // backend has already counted this one, so GANHO takes it anyway —
                // it just has no landing to show for it.
                else if (prize > 0) creditPrize(prize)

                /* THE CUE for saida_ganhou.wav: the last bird landing. The piece is
                   the round being handed over, so it opens on the thing the round was
                   for, and then plays across PARABÉNS, the GANHO counting up and the
                   scene clearing.
                   It used to open on the PULAR press — `over` is set off the attempt
                   counter and that counter goes up on the press — so its first four
                   seconds ran under pula_urso.wav and then acertou_cesta.wav, three
                   voices at once, with the background loop cut mid-phrase underneath.
                   On a CATCH the cheer is already sounding when the bird arrives: it
                   starts at LEAP_MS and the flight only lands at ~1665ms, so the catch
                   is heard 750ms before it is seen. Opening the piece on this frame
                   therefore cut the cheer off 1.2s into its 1.95s — the fifth basket
                   sounded wrong precisely because the cheer was still mid-word. So the
                   piece lets the cheer get its word out and comes in over the tail of
                   it, EXIT_LEAD_MS before it dies away: press+1865ms, 200ms after the
                   bird is in the basket.
                   A miss has no cheer and no basket, but the bird has finished falling
                   by here and the round still has to end, so it opens straight away. */
                if (lastThrowRef.current) {
                    lastThrowRef.current = false
                    const wait = hitRef.current
                        ? (cheerStartRef.current + CHEER_TAIL_MS - EXIT_LEAD_MS) - Date.now()
                        : 0
                    if (wait > 0) {
                        clearTimeout(exitStartRef.current)
                        exitStartRef.current = setTimeout(() => startExitRef.current(), wait)
                    } else startExitRef.current()
                }
                nextPenguin()
            },
        })
        // no spin: the bird stays upright the whole way over, only the arc moves it.
        // One linear clock drives the whole path so the aim can be re-read per frame;
        // the shape is unchanged — level speed across, power2.out up to the apex,
        // power2.in down out of it. A miss adds a second leg: from the point beside
        // the basket it falls, gathering speed, until the bottom edge clips it.
        tl.to(st, {
            t: 1, duration: flight, ease: 'none',
            onUpdate: () => {
                const g = target()
                const t = st.t
                let pos
                if (caught || t <= MISS_AT) {
                    pos = arcAt(caught ? t : t / MISS_AT, g)
                } else {
                    if (!fallFrom) fallFrom = arcAt(1, g)
                    const f = (t - MISS_AT) / (1 - MISS_AT)
                    pos = {
                        // barely any sideways drift: it slides down the outside of
                        // the basket rather than away from it
                        x: fallFrom.x + s.width * 0.012 * f,
                        y: fallFrom.y + (s.bottom + p.height - fallFrom.y) * drop(f),
                    }
                }
                gsap.set(peng, { x: base.x + (pos.x - from.x), y: base.y + (pos.y - from.y) })
            },
        }, 0)
        if (caught) {
            // tuck into the basket at the very end
            tl.to(peng, { scale: 0.72, duration: 0.18, ease: 'power2.in' }, flight - 0.18)
            // gone exactly as it arrives, so the basket's own sprite takes over on the
            // same frame rather than 20ms after the bird has stopped moving
            tl.to(peng, { opacity: 0, duration: 0.1 }, flight - 0.1)
        }
        // a missed bird keeps its size the whole way down and is simply clipped by
        // the bottom edge — no tuck, no fade, nothing to suggest it was caught
    }
    catapultRef.current = catapult
    slamRef.current = slam

    /* ---- a hit: the basket carries the penguin away with its prize ---- */
    const landInBasket = (balloon) => {
        // what getBonusResult paid for this throw's tile — nothing else ever goes
        // on screen, so a shot the backend did not pay for is worth 0
        const prize = shotPrizeRef.current || 0
        // The bird riding in the basket has to be the one that was just thrown.
        // This used to pick the cell off the BALLOON's id, so a thin penguin could
        // go up and a round one arrive. Read from the ref, not the state: this
        // runs from the flight's onComplete, where `activeChar` is already stale.
        const riding = PENG_CHARS[activeCharRef.current].basketFrame
        setBalloons((b) => b.map((x) => x.id === balloon.id
            ? { ...x, frame: riding, prize } : x))

        // the title flips to PARABÉNS!!! for a beat, as in the video
        setCongrats(true)
        gsap.delayedCall(1.4, () => setCongrats(false))

        creditPrize(prize)
    }

    /* ---- GANHO counts the prize up.
       The count-up is written straight to the DOM node. It used to setGanho on every
       tick, which re-rendered the whole scene — background, every balloon, both
       penguins, the meters — ~60 times over the 0.7s starting exactly at the
       landing. That render storm is what made the drop into the basket brake. ---- */
    const creditPrize = (prize) => {
        if (!prize) return
        const target = ganhoRef.current + prize
        const from = { v: ganhoRef.current }
        ganhoRef.current = target
        gsap.to(from, {
            v: target, duration: 0.7, ease: 'power1.out',
            onUpdate: () => {
                if (ganhoElRef.current) ganhoElRef.current.textContent = Math.round(from.v)
            },
            // one render at the end, to put React's tree back in step with the DOM
            onComplete: () => setGanho(target),
        })
    }

    /* ---- the round is spent: clear the scene, which is what sets `pengGone` and
       so what finally closes the bonus.
       `fadeActive` is the difference between the two ways a round ends. On the last
       throw the active bird is already away — tucked into a basket or clipped off
       the bottom — so it is simply unmounted, as before. When the CLOCK is what
       ended it, that bird is still standing there in plain sight and has to be
       faded out too. ---- */
    const endRound = (fadeActive) => {
        if (fadeActive && pengRef.current) {
            gsap.to(pengRef.current, {
                opacity: 0, duration: 0.35, ease: 'power1.in',
                onComplete: () => setPengGone(true),
            })
        } else {
            setPengGone(true)
        }
        const cliff = cliffRef.current
        if (!cliff) { setCliffFull(false); return }
        // Fade the waiting bird rather than blinking it out — by this point it
        // is the only thing left standing and a one-frame disappearance shows.
        // Its own entrance keyframe has to be cleared first: `bk-peng-return`
        // is `both`, and an animation's opacity outranks an inline one, so GSAP
        // would have no effect while it is still attached.
        cliff.style.animation = 'none'
        gsap.to(cliff, {
            opacity: 0, duration: 0.35, ease: 'power1.in',
            onComplete: () => setCliffFull(false),
        })
    }
    // the clock's effect is declared above this, so it reaches endRound by ref
    const endRoundRef = useRef(endRound)
    endRoundRef.current = endRound

    /* ---- next attempt: the penguin queued on the pillar becomes the active one
       and hops down, which frees the pillar for the next one to drop in ---- */
    const nextPenguin = () => {
        // the throw is down; nothing is in the air until the next PULAR
        inFlightRef.current = false
        // Nothing follows the last shot. Read off the ref, not `over`: this runs
        // from the flight's onComplete, whose closure predates the round ending.
        // The pillar empties and the plank stays bare from here — the row is full
        // (or the attempts are spent), so there is nothing left to throw.
        if (overRef.current) {
            endRound(false)
            return
        }
        gsap.delayedCall(0.3, () => {
            setCliffFull(false)          // the queued one hands over…
            // …and it is the SAME bird that carries on: the one the player has
            // been looking at on the pillar is the one that comes down next, so
            // its draw moves across rather than being made again.
            setActiveChar(queuedChar)
            setQueuedChar(pickPengChar())
            // it takes over up on the pillar, so it starts sitting like the one it
            // is replacing — set with the key, or it would flash standing for a frame
            setStanding(false)
            setPengKey((k) => k + 1)     // …to a fresh active penguin in its place
        })
    }

    // One bird, two drawings — sitting up on the pillar, standing on the plank. The
    // box is given each image's own width so nothing is squeezed, and `left` puts
    // each drawing's FIGURE (not its box) on the shared centre, so the pillar bird,
    // the plank bird and the two poses of the same bird all stand over one point.
    const pengArtStyle = (art) => {
        // the box's height first, in shared units, then the width its own aspect
        // asks for — `scale` says how tall this file's canvas stands in those
        // units and `h` how tall it is in its own pixels, so a file cropped tight
        // to the figure sits at the same size as one on the padded 228 canvas.
        const h = art.h || PENG_ART_H
        const height = (PENG_BOX_W * (art.scale || PENG_ART_H)) / PENG_BOX_REF_W
        const width = (height * art.w) / h
        return {
            backgroundImage: `url(${art.src})`,
            backgroundSize: '100% 100%',
            width: `${width}%`,
            aspectRatio: `${art.w} / ${h}`,
            left: `${PENG_BOX_CENTRE - width * (art.cx || 0.5)}%`,
        }
    }
    const activeArt = PENG_CHARS[activeChar]
    const pengStyle = pengArtStyle(standing ? activeArt.art : activeArt.pillar)
    // whoever is waiting up on the pillar is sitting: it has not been down yet
    const cliffPengStyle = pengArtStyle(PENG_CHARS[queuedChar].pillar)

    // `bearFrame` null means idle: the two-frame breathe loop, stepped by CSS.
    // Its position is left to the keyframes — the jump sets its own inline, and
    // the class simply stops applying, so the two never fight over the property.
    const bearIdle = bearFrame == null
    const bearStyle = bearIdle
        ? {
            backgroundImage: `url(${BEAR_IDLE_STRIP})`,
            backgroundSize: `${BEAR_IDLE_FRAMES * 100}% 100%`,
        }
        : {
            backgroundImage: `url(${BEAR_STRIP})`,
            backgroundSize: `${BEAR_FRAMES * 100}% 100%`,
            backgroundPositionX: `${(bearFrame * 100) / (BEAR_FRAMES - 1)}%`,
        }

    return (
        <div className='basket-bonus' ref={stageRef}>
            <img
                className='position-absolute start-0 left-0 end-0 bottom-0 h-100 w-100'
                src={`${BASE}/background.png`}
                alt=''
            />

            {/* title — flips to PARABÉNS!!! on a hit */}
            <img key={String(congrats)} className={`bk-msg${congrats ? ' is-win' : ''}`}
                src={`${BASE}/${congrats ? 'msg_congrats.png' : 'msg_try.png'}`} alt='' />

            {/* balloons + baskets drifting up past the scene */}
            {balloons.map((b) => (
                <Balloon key={b.id} {...b}
                    registerRef={(el) => { balloonEls.current[b.id] = el }}
                    onDone={() => removeBalloon(b.id)} />
            ))}

            {/* the penguin queued on the pillar, waiting its turn */}
            {cliffFull && (
                <div ref={cliffRef} className='bk-peng-cliff bk-peng-queued'
                    style={cliffPengStyle} />
            )}

            {/* the penguin in play — starts on the pillar, hops to the plank,
                then gets catapulted. Keyed so each attempt gets a fresh one.
                Held on `pengGone`, never on `over`: the last PULAR ends the round
                while the bird it threw is still in the air, and unmounting on
                `over` would snatch that bird out of mid-flight. */}
            {!pengGone && (
                <div key={pengKey} ref={pengRef} className='bk-peng-cliff'
                    style={pengStyle} />
            )}

            {/* the bear: looping idle gif, swapped for the stepped jump strip */}
            <div ref={bearRef} className={`bk-bear${bearIdle ? ' is-idle' : ''}`}
                style={bearStyle} />

            {/* FORÇA bar + TEMPO dial. spritePower.png carries the bar, the
                "TEMPO" label and the black socket the pie sits in, so the pie is
                nested inside it and positioned in % of the bar. */}
            <div className='bk-meters'>
                <div className='bk-power' style={{
                    backgroundImage: `url(${POWER.img})`,
                    backgroundSize: `100% ${POWER.frames * 100}%`,
                    backgroundPositionY: `${(power * 100) / (POWER.frames - 1)}%`,
                }} />
                <div className='bk-time' style={{
                    backgroundImage: `url(${TIME.img})`,
                    backgroundSize: `${TIME.frames * 100}% 100%`,
                    backgroundPositionX: `${(timeStep * 100) / (TIME.frames - 1)}%`,
                }} />
            </div>

            {/* PULAR — 2-state sprite: left half normal, right half pressed */}
            <button
                type='button'
                className='bk-launch'
                style={{ backgroundImage: `url("${LAUNCH_BTN}")` }}
                onClick={onLaunchClick}
                disabled={!onPlank || over}
                aria-label='Pular'
            />

            {/* GANHO box — the label is baked into the art, the running total
                is printed on the wooden board underneath it */}
            <div className='bk-ganho'>
                <img src={`${BASE}/winbox.png`} alt='' />
                <span className='bk-ganho-val' ref={ganhoElRef}>{ganho}</span>
            </div>

            {/* the row along the bottom — the five chances. One X lands per PULAR,
                stamped as the throw goes off, win or miss, so the row always ends
                the round fully crossed */}
            {ROW.map((left, i) => (
                <div key={i} className='bk-peng' style={{ left: `${left}%` }}>
                    <img src={PENG_MARKER} alt='' />
                    {i < attempt && (
                        <img className='bk-x' src={`${BASE}/x.png`} alt='' />
                    )}
                </div>
            ))}
        </div>
    )
}

export default BasketBonus
